'use client';

import {
  type ChangeEvent,
  type Key,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import { ComboBox } from '@/lib/base/forms/combobox';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { InputBase } from '@/lib/base/forms/input/input';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { EntityTypeahead } from '@/lib/crm/components/EntityTypeahead';
import { logger } from '@/lib/logger';
import type {
  FeedbackCategory,
  StructuredFeedbackData,
} from '@/lib/reasoning/feedback';
import type { EntityResult } from '@/lib/search';
import { sanitizeError } from '@/lib/utils/errors';

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: StructuredFeedbackData) => Promise<void>;
  chainId: number;
  orgId: number;
  steps?: Array<{ id: number; step_number: number }>;
}

const CATEGORY_OPTIONS = [
  { id: 'hallucination', label: 'Made something up' },
  { id: 'wrong_entity', label: 'Wrong person, company, or deal' },
  { id: 'wrong_value', label: 'Incorrect amount, date, or detail' },
  { id: 'incomplete', label: 'Incomplete or missing information' },
  { id: 'wrong_tone', label: 'Wrong tone or style' },
  { id: 'unhelpful', label: 'Not useful' },
  { id: 'other', label: 'Other' },
];

// =============================================================================
// Inner component — must be inside FormProvider
// =============================================================================

interface FeedbackModalContentProps extends FeedbackModalProps {
  category: FeedbackCategory | null;
  setCategory: (cat: FeedbackCategory | null) => void;
  correctEntity: EntityResult | null;
  setCorrectEntity: (entity: EntityResult | null) => void;
  correctValue: string;
  setCorrectValue: (value: string) => void;
}

function FeedbackModalContent({
  isOpen,
  onClose,
  onSubmit,
  chainId: _chainId,
  orgId,
  steps = [],
  category,
  setCategory,
  correctEntity,
  setCorrectEntity,
  correctValue,
  setCorrectValue,
}: FeedbackModalContentProps) {
  const { setSubmitError, resetField } = useFormContext();
  const [comment, setComment] = useState('');
  const [flaggedStepId, setFlaggedStepId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setCategory(null);
    setComment('');
    setFlaggedStepId(null);
    setCorrectEntity(null);
    setCorrectValue('');
    setSubmitError(null);
  }, [setCategory, setCorrectEntity, setCorrectValue, setSubmitError]);

  const handleSubmit = useCallback(async () => {
    const data: StructuredFeedbackData = {
      rating: 'negative',
      category: category || undefined,
      comment: comment || undefined,
      flagged_step_id: flaggedStepId || undefined,
    };

    // Build correction_data based on category with explicit type guards
    if (
      category === 'wrong_entity' &&
      correctEntity?.type &&
      correctEntity?.id
    ) {
      data.correction_data = {
        correct_entity_type: correctEntity.type,
        correct_entity_id: correctEntity.id,
      };
    } else if (category === 'wrong_value' && correctValue.trim()) {
      data.correction_data = {
        correct_value: correctValue,
      };
    }

    try {
      setIsSubmitting(true);
      await onSubmit(data);
      resetForm();
      onClose();
    } catch (error) {
      logger.error(
        'Error submitting feedback',
        error instanceof Error ? error : undefined,
        { component: 'FeedbackModal' }
      );
      setSubmitError(
        sanitizeError(
          error instanceof Error
            ? error.message
            : "We couldn't submit that feedback. Try again."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    category,
    comment,
    flaggedStepId,
    correctEntity,
    correctValue,
    setSubmitError,
    onSubmit,
    onClose,
    resetForm,
  ]);

  const handleClose = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleCategoryChange = useCallback(
    (key: Key | null) => {
      setCategory(key && key !== '' ? (key as FeedbackCategory) : null);
      setCorrectEntity(null);
      setCorrectValue('');
      resetField('correctEntity');
      resetField('correctValue');
    },
    [resetField, setCategory, setCorrectEntity, setCorrectValue]
  );

  const handleCorrectEntityChange = useCallback(
    (entity: EntityResult | EntityResult[] | null) => {
      setCorrectEntity(Array.isArray(entity) ? null : entity);
    },
    [setCorrectEntity]
  );

  const handleStepChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setFlaggedStepId(e.target.value ? Number(e.target.value) : null);
    },
    [setFlaggedStepId]
  );

  const handleCommentChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setComment(e.target.value);
    },
    [setComment]
  );

  const footer = useMemo(
    () => (
      <div className='flex justify-end gap-2'>
        <Button color='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <SubmitButton
          color='primary'
          onClick={handleSubmit}
          isDisabled={isSubmitting}
        >
          Submit feedback
        </SubmitButton>
      </div>
    ),
    [handleClose, handleSubmit, isSubmitting]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className='max-w-md bg-bg-secondary'>
        <Dialog
          title='Help us improve'
          description="Tell us what didn't work so we can do better."
          footer={footer}
        >
          <div className='flex flex-col gap-4'>
            {/* Category selection */}
            <ComboBox
              aria-label='Feedback category'
              items={CATEGORY_OPTIONS}
              selectedKey={category}
              onSelectionChange={handleCategoryChange}
              placeholder='Select an issue'
            >
              {item => <SelectItem id={item.id}>{item.label}</SelectItem>}
            </ComboBox>

            {/* Conditional: Entity search for wrong_entity */}
            {category === 'wrong_entity' && (
              <FormField name='correctEntity' label='Correct record'>
                {() => (
                  <EntityTypeahead
                    value={correctEntity || undefined}
                    onChange={handleCorrectEntityChange}
                    orgId={orgId}
                    placeholder='Search for correct record'
                  />
                )}
              </FormField>
            )}

            {/* Conditional: Text input for wrong_value */}
            {category === 'wrong_value' && (
              <FormField name='correctValue' label='Correct value'>
                {({ isInvalid, inputId }) => (
                  <InputBase
                    id={inputId}
                    value={correctValue}
                    onChange={setCorrectValue}
                    placeholder='Enter the correct value'
                    isInvalid={isInvalid}
                    isDisabled={isSubmitting}
                  />
                )}
              </FormField>
            )}

            {/* Step selector (if steps available) */}
            {steps.length > 0 && (
              <div>
                <label className='block text-sm font-medium mb-1 text-[var(--color-text-secondary)]'>
                  Which step was wrong? (optional)
                </label>
                <NativeSelect
                  value={flaggedStepId ? String(flaggedStepId) : ''}
                  onChange={handleStepChange}
                  options={[
                    { label: 'Select a step', value: '' },
                    ...steps.map(step => ({
                      label: `Step ${step.step_number}`,
                      value: String(step.id),
                    })),
                  ]}
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* Comment field */}
            <div>
              <label
                htmlFor='feedback-comment'
                className='block text-sm font-medium mb-1 text-[var(--color-text-secondary)]'
              >
                Additional comments (optional)
              </label>
              <textarea
                id='feedback-comment'
                value={comment}
                onChange={handleCommentChange}
                placeholder='Tell us more'
                rows={3}
                className='w-full input-refined resize-none'
              />
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

// =============================================================================
// Outer component — holds conditional state + FormProvider
// =============================================================================

export function FeedbackModal(props: FeedbackModalProps) {
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [correctEntity, setCorrectEntity] = useState<EntityResult | null>(null);
  const [correctValue, setCorrectValue] = useState('');

  const fieldConfig = useMemo<Record<string, FieldConfig>>(() => {
    const config: Record<string, FieldConfig> = {};
    if (category === 'wrong_entity') {
      config.correctEntity = { required: true };
    }
    if (category === 'wrong_value') {
      config.correctValue = { required: true };
    }
    return config;
  }, [category]);

  const formValues = useMemo<Record<string, unknown>>(() => {
    if (category === 'wrong_entity') {
      return { correctEntity };
    }
    if (category === 'wrong_value') {
      return { correctValue };
    }
    return {};
  }, [category, correctEntity, correctValue]);

  return (
    <FormProvider fields={fieldConfig} values={formValues}>
      <FeedbackModalContent
        {...props}
        category={category}
        setCategory={setCategory}
        correctEntity={correctEntity}
        setCorrectEntity={setCorrectEntity}
        correctValue={correctValue}
        setCorrectValue={setCorrectValue}
      />
    </FormProvider>
  );
}
